import React, { useEffect, useRef } from 'react';
import {
  List, Datagrid, TextField, NumberField, DateField, EditButton, BooleanField,
  Edit, Create, TabbedForm, FormTab, TextInput, NumberInput, BooleanInput, SelectInput,
  ArrayInput, SimpleFormIterator, FormDataConsumer, Filter,
  ImageInput, ImageField,
  required, number, minValue
} from 'react-admin';
import { useForm } from 'react-final-form';
import Box from '@material-ui/core/Box';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { NoDeleteToolbar, CreativePreviewContent } from '../components/utils';

// Must match CONSOLE_CTYPES in insights/routes/console/advertiser_write_policy.js
// (NOT the narrower CTYPES in routes/api/v1/validators.js - `image` is
// console-only, because base64-over-API is a second ingest path with no real
// benefit). There is no "banner" type, whatever older docs may say.
const CTYPE_CHOICES = [
  { id: 'image', name: 'Image (upload)' },
  { id: 'url', name: 'Image (hosted URL)' },
  { id: 'html', name: 'HTML markup' },
  { id: 'video', name: 'Video' },
  { id: 'native', name: 'Native' }
];

// Mirrors the server's accepted formats, which are enforced by MAGIC BYTES in
// service/creative_asset_upload.js - this attribute only filters the file
// picker, it is not a security control. SVG is deliberately absent: it is
// script-capable and the server refuses it.
const UPLOAD_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';
const UPLOAD_MAX_MB = 2;

const CreativeFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
  </Filter>
);

export const AdvertiserCreativeList = (props) => (
  <List {...props} filters={<CreativeFilter />} perPage={25} bulkActionButtons={false}>
    <Datagrid rowClick="edit">
      <TextField source="code" label="ID" />
      <TextField source="name" label="Creative" />
      <TextField source="ctype" label="Type" />
      <NumberField source="width" label="W" />
      <NumberField source="height" label="H" />
      <BooleanField source="active" label="Active" />
      <DateField source="updated_at" label="Updated" />
      <EditButton variant="contained" color="primary" />
    </Datagrid>
  </List>
);

// Mirrors the server's isHttpUrl exactly (insights/routes/api/v1/validators.js:231-233:
// /^https?:\/\/.+/), not a stricter pattern, so anything that passes here is
// guaranteed to also pass server validation instead of round-tripping into an
// unguaranteed-to-surface server rejection.
const validateHttpUrl = (value) => (
  !value || /^https?:\/\/.+/.test(value) ? undefined : 'Must be an http(s) URL'
);

// Type-specific fields, keyed by the ctype they belong to. Used below to
// clear out-of-type values when ctype changes on Create (fields shared across
// every type - name, active, iab_categories, the tracker arrays - are never
// touched and are deliberately absent from this map).
const TYPE_SPECIFIC_FIELDS_BY_CTYPE = {
  image: ['width', 'height', 'image', 'click_url'],
  url: ['width', 'height', 'image_url', 'click_url'],
  html: ['width', 'height', 'markup'],
  video: ['video_url', 'video_duration', 'video_mime', 'video_click_url'],
  native: ['title', 'image_url', 'click_url', 'description', 'sponsor', 'call_to_action_button_text']
};
const ALL_TYPE_SPECIFIC_FIELDS = [
  ...new Set([].concat(...Object.values(TYPE_SPECIFIC_FIELDS_BY_CTYPE)))
];

// react-final-form does not clear a field's value just because its input
// unmounted - switching ctype on Create only changes which inputs render, it
// never touches form state for the fields that disappear. Every one of those
// stale values (e.g. image_url left over from "url") is on
// CONSOLE_CREATIVE_WRITABLE, so without this it would ride along in the
// submitted body and get silently persisted onto a creative of a type it has
// no business describing - no error, no rejection, just a wrong document.
// ctype is immutable on Edit (read-only field), so this is a no-op there.
const ClearStaleTypeFields = ({ ctype }) => {
  const form = useForm();
  const previousCtype = useRef(ctype);

  useEffect(() => {
    if (previousCtype.current === ctype) return;
    const keep = TYPE_SPECIFIC_FIELDS_BY_CTYPE[ctype] || [];
    ALL_TYPE_SPECIFIC_FIELDS
      .filter(field => !keep.includes(field))
      .forEach(field => form.change(field, undefined));
    previousCtype.current = ctype;
  }, [ctype, form]);

  return null;
};

// Plain array-returning helpers, not components rendered as <X/>: FormTab
// clones record/resource/basePath/variant/margin onto each LITERAL child in its
// `children` prop. A function component used as a single opaque child (e.g.
// <SharedFields/>) would receive those injected props and, unless it
// re-forwarded every one of them by name, silently drop them before they ever
// reach the real inputs - exactly the bug fixed in campaigns.js (commit
// c00f7b8). Arrays of elements, unlike component-wrapped children, are
// flattened by React's Children utilities, so each entry below still gets its
// own individual per-field treatment as if it had been written inline.
//
// The same rule is why the FormTabs below are written inline in Create/Edit
// rather than returned from a helper component: TabbedForm needs FormTab itself
// to be its direct child for tab/label bookkeeping.
// react-admin applies its per-input spacing (the FormInput wrapper: block
// display plus margins) only to LITERAL children of FormTab. Anything returned
// from a FormDataConsumer render function is rendered raw, so a bare fragment
// left these inputs with no layout at all - Width and Height sat flush against
// each other and read as one merged control, and a fullWidth input bled past
// the tab's padding. The ops form solves this with <div> plus <br/> tags; this
// is the same fix with real spacing instead.
const TypeFieldsLayout = ({ children }) => (
  <Box display="flex" flexDirection="column" width="100%"
    style={{ gap: '8px', marginTop: '8px' }}>
    {children}
  </Box>
);

// Width and Height belong together, so they get an explicit row rather than
// inheriting the column above.
const sizeFields = () => (
  <Box key="size" display="flex" style={{ gap: '24px' }}>
    <NumberInput source="width" label="Width" step={1}
      validate={[required(), number(), minValue(1)]} />
    <NumberInput source="height" label="Height" step={1}
      validate={[required(), number(), minValue(1)]} />
  </Box>
);

// Required sets mirror the per-ctype branches at validators.js:305-328.
const typeSpecificFields = () => (
  <FormDataConsumer key="type-specific">
    {({ formData }) => {
      if (!formData) return null;
      if (formData.ctype === 'image') {
        return (
          <TypeFieldsLayout>
            <ClearStaleTypeFields ctype={formData.ctype} />
            {sizeFields()}
            {/* The App.js dataProvider interceptor turns the picked file into a
                base64 `file_data` field on save; the server validates it and
                writes the stored asset back onto `image` itself, so this input
                never carries the final URL. `required()` is not used: on Edit
                the creative already HAS an image, and the picker is empty until
                a replacement is chosen - requiring it would block every edit
                that is not a re-upload. A create with no file is refused
                server-side. */}
            <ImageInput source="image" label={`Image file (max ${UPLOAD_MAX_MB} MB)`}
              accept={UPLOAD_ACCEPT}
              placeholder={<p>Drop a PNG, JPEG, GIF or WebP here, or click to choose one.</p>}>
              <ImageField source="src" title="title" />
            </ImageInput>
            <TextInput source="click_url" label="Click URL" fullWidth validate={[required(), validateHttpUrl]} />
          </TypeFieldsLayout>
        );
      }
      if (formData.ctype === 'url') {
        return (
          <TypeFieldsLayout>
            <ClearStaleTypeFields ctype={formData.ctype} />
            {sizeFields()}
            <TextInput source="image_url" label="Image URL" fullWidth validate={[required(), validateHttpUrl]} />
            <TextInput source="click_url" label="Click URL" fullWidth validate={[required(), validateHttpUrl]} />
          </TypeFieldsLayout>
        );
      }
      if (formData.ctype === 'html') {
        return (
          <TypeFieldsLayout>
            <ClearStaleTypeFields ctype={formData.ctype} />
            {sizeFields()}
            <TextInput source="markup" label="HTML Markup" multiline fullWidth
              minRows={6} validate={required()} />
          </TypeFieldsLayout>
        );
      }
      if (formData.ctype === 'video') {
        return (
          <TypeFieldsLayout>
            <ClearStaleTypeFields ctype={formData.ctype} />
            <TextInput source="video_url" label="Video URL" fullWidth validate={[required(), validateHttpUrl]} />
            <Box display="flex" style={{ gap: '24px' }}>
              <NumberInput source="video_duration" label="Duration (seconds)" step={1}
                validate={[required(), number(), minValue(1)]} />
              <TextInput source="video_mime" label="MIME type" defaultValue="video/mp4" validate={required()} />
            </Box>
            <TextInput source="video_click_url" label="Click URL" fullWidth validate={validateHttpUrl} />
          </TypeFieldsLayout>
        );
      }
      if (formData.ctype === 'native') {
        return (
          <TypeFieldsLayout>
            <ClearStaleTypeFields ctype={formData.ctype} />
            <TextInput source="title" label="Title" fullWidth validate={required()} />
            <TextInput source="image_url" label="Image URL" fullWidth validate={[required(), validateHttpUrl]} />
            <TextInput source="click_url" label="Click URL" fullWidth validate={[required(), validateHttpUrl]} />
            <TextInput source="description" label="Description" fullWidth />
            <Box display="flex" style={{ gap: '24px' }}>
              <TextInput source="sponsor" label="Sponsor" />
              <TextInput source="call_to_action_button_text" label="Call to action" />
            </Box>
          </TypeFieldsLayout>
        );
      }
      return null;
    }}
  </FormDataConsumer>
);

// Fields common to Create and Edit. Returned as an array (see the note on
// sizeFields above) so it can be spread into the FormTab's children as literal
// direct children instead of being hidden behind a wrapper component.
const sharedFields = () => ([
  <BooleanInput key="active" source="active" label="Active" defaultValue={true} />,
  <TextInput key="name" source="name" label="Creative Name" validate={required()} style={{ width: '400px' }} />,
  typeSpecificFields(),
  <ArrayInput key="iab_categories" source="iab_categories" label="IAB Categories">
    <SimpleFormIterator><TextInput label="Category" /></SimpleFormIterator>
  </ArrayInput>,
  <ArrayInput key="impression_trackers" source="impression_trackers" label="Impression Trackers">
    <SimpleFormIterator><TextInput label="URL" fullWidth /></SimpleFormIterator>
  </ArrayInput>,
  <ArrayInput key="click_trackers" source="click_trackers" label="Click Trackers">
    <SimpleFormIterator><TextInput label="URL" fullWidth /></SimpleFormIterator>
  </ArrayInput>
]);

// The live preview, rendered from current form state on both Create and Edit.
// Same renderer and same tab label/icon as the ops creative form
// (src/creatives.js), so a creative looks identical in both consoles.
const previewTabChildren = () => ([
  <FormDataConsumer key="preview">
    {({ formData }) => (
      <Box style={{ padding: '16px 0', width: '100%' }}>
        <CreativePreviewContent formData={formData} />
      </Box>
    )}
  </FormDataConsumer>
]);

export const AdvertiserCreativeCreate = (props) => (
  <Create title="New Creative" {...props}>
    <TabbedForm toolbar={<NoDeleteToolbar />}>
      <FormTab label="Creative Settings">
        <SelectInput source="ctype" label="Creative Type" choices={CTYPE_CHOICES}
          validate={required()} helperText="Cannot be changed after the creative is created." />
        {sharedFields()}
      </FormTab>
      <FormTab label="Preview" icon={<VisibilityIcon />}>
        {previewTabChildren()}
      </FormTab>
    </TabbedForm>
  </Create>
);

export const AdvertiserCreativeEdit = (props) => (
  <Edit title="Creative" undoable={false} {...props}>
    <TabbedForm toolbar={<NoDeleteToolbar />}>
      <FormTab label="Creative Settings">
        <TextField source="code" label="Creative ID" />
        {/* ctype is immutable after create and is stripped server-side on update. */}
        <TextField source="ctype" label="Creative Type" />
        {sharedFields()}
      </FormTab>
      <FormTab label="Preview" icon={<VisibilityIcon />}>
        {previewTabChildren()}
      </FormTab>
    </TabbedForm>
  </Edit>
);
